export class Markdown {
	static parse(markdown: string): string {
		if (!markdown) { return ''; }
		return this.processLines(markdown);
	}
	
	private static processLines(markdown: string): string {
		const lines = markdown.split('\n');
		let output: string[] = [];
		
		let state = {
			inList: false,
			listType: null as 'ul' | 'ol' | null,
			inCodeBlock: false,
			inParagraph: false,
			inBlockquote: false
		};
		
		const closeList = () => {
			if (state.inList) {
				output.push(state.listType === 'ul' ? '</ul>' : '</ol>');
				state.inList = false;
				state.listType = null;
			}
		};
		
		const closeParagraph = () => {
			if (state.inParagraph) {
				output.push('</p>');
				state.inParagraph = false;
			}
		};
		
		const closeBlockquote = () => {
			if (state.inBlockquote) {
				output.push('</blockquote>');
				state.inBlockquote = false;
			}
		};
		
		const closeAll = () => {
			closeList();
			closeParagraph();
			closeBlockquote();
		};
		
		for (let i = 0; i < lines.length; i++) {
			let line = lines[i];
			const trimmed = line.trim();
			
			// Code Blocks
			if (trimmed.startsWith('```')) {
				closeAll();
					
				if (state.inCodeBlock) {
					output.push('</code></pre>');
					state.inCodeBlock = false;
				} else {
					output.push('<pre><code>');
					state.inCodeBlock = true;
				}
				
				continue;
			}
			
			if (state.inCodeBlock) {
				output.push(this.escapeHtml(line) + '\n');
				continue;
			}
			
			// Headers
			// Matches # Header, ## Header, etc.
			const headerMatch = line.match(/^(#{1,6})\s*(.+)$/);
			
			if (headerMatch) {
				closeAll();
				const level = headerMatch[1].length;
				const content = headerMatch[2];
				output.push(`<h${level}>${this.formatInline(content)}</h${level}>`);
				continue;
			}
			
			// Horizontal Rule
			if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
				closeAll();
				output.push('<hr>');
				continue;
			}
			
			// Unordered Lists (* or -)
			const ulMatch = line.match(/^(\s*)([-*])\s+(.+)$/);
			
			if (ulMatch) {
				closeParagraph();
				closeBlockquote();
				if (state.inList && state.listType === 'ol') { closeList(); }
				
				if (!state.inList) {
					output.push('<ul>');
					state.inList = true;
					state.listType = 'ul';
				}
				
				output.push(`<li>${this.formatInline(ulMatch[3])}</li>`);
				continue;
			}
			
			// Ordered Lists (1. or 1))
			const olMatch = line.match(/^(\s*)(\d+)[\.)]\s+(.+)$/);
			
			if (olMatch) {
				closeParagraph();
				closeBlockquote();
				if (state.inList && state.listType === 'ul') { closeList(); }

				if (!state.inList) {
					output.push('<ol>');
					state.inList = true;
					state.listType = 'ol';
				}
				
				output.push(`<li>${this.formatInline(olMatch[3])}</li>`);
				continue;
			}
			
			// Blockquotes
			if (trimmed.startsWith('>')) {
				closeParagraph();
				closeList();
				
				if (!state.inBlockquote) {
					output.push('<blockquote>');
					state.inBlockquote = true;
				}
				
				// Handle nested blockquote content
				const content = trimmed.substring(1).trim();
				output.push(`${this.formatInline(content)}<br>`);
				continue;
			}
			
			// End List/Blockquote if empty line
			if (trimmed === '') {
				closeAll();
				continue;
			}
			
			// Paragraphs
			closeList();
			closeBlockquote();
				
			if (!state.inParagraph) {
				output.push('<p>');
				state.inParagraph = true;
			} else {
				output.push(' '); // Space between lines in same paragraph
			}
			
			output.push(this.formatInline(line));
		}
		
		closeAll();
		if (state.inCodeBlock) { output.push('</code></pre>'); }
		return output.join('');
	}
	
	private static formatInline(text: string): string {
		let html = this.escapeHtml(text);
		
		// Bold
		html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
		html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
		
		// Italic
		html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
		html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
		
		// Inline Code
		html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
		
		return html;
	}
	
	private static escapeHtml(text: string): string {
		return text
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#039;");
	}
}
